import React, { useEffect } from 'react';
import CircularProgress from 'components/atoms/CircularProgress';

import { createTheme } from '@material-ui/core/styles';
import { ThemeProvider } from '@material-ui/styles';
import { Redirect, Route, Switch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { IntlProvider } from 'react-intl';

import { setInitUrl, userSignInSuccess, authCheckStart, authCheckComplete } from 'actions/Auth';
import { isValidJSON } from 'util/functions';

import { setDarkTheme, setThemeColor } from 'actions/Setting';
import { RestrictedRoute } from 'app/routes';
import SignIn from 'app/routes/SignIn';
import ForgotPassword from 'app/routes/Password/ForgotPassword';
import PasswordRecovery from 'app/routes/Password/RecoveryPassword';
import ToasterContainer from 'components/organisms/ToasterContainer';
import AppLayout from 'components/templates/AppLayout';
import useThemeColor from 'hooks/useThemeColor';
import AppLocale from 'lngProvider';
import indigoTheme from 'themes/indigoTheme';
import { TimezoneProvider } from 'contexts/TimezoneContext';

import 'assets/vendors/style';
import IntlMessages from 'util/IntlMessages';

const App = ({ match, location, history }) => {
  const dispatch = useDispatch();
  const { authUser, initURL, authLoading } = useSelector(({ auth }) => auth);
  const { getColorTheme } = useThemeColor();
  const { themeColor, darkTheme, locale, isDirectionRTL } = useSelector(({ settings }) => settings);
  const currentAppLocale = AppLocale[locale.locale];

  useEffect(() => {
    window.__MUI_USE_NEXT_TYPOGRAPHY_VARIANTS__ = true;
    dispatch(authCheckStart());

    const checkUserAuthentication = async () => {
      try {
        let user = localStorage.getItem('user_data');

        if (user && isValidJSON(user)) {
          const parsedUser = JSON.parse(user);
          dispatch(userSignInSuccess(parsedUser));
        } else {
          localStorage.removeItem('user_data');
          dispatch(authCheckComplete());
        }
      } catch (error) {
        localStorage.removeItem('user_data');
        dispatch(userSignInSuccess(null));
        dispatch(authCheckComplete());
      }
    };

    checkUserAuthentication();

    if (initURL === '') {
      dispatch(setInitUrl(history.location.pathname));
    }
    const params = new URLSearchParams(location.search);
    if (params.has('theme-name')) {
      dispatch(setThemeColor(params.get('theme-name')));
    }
    if (params.has('dark-theme')) {
      dispatch(setDarkTheme());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isDarkTheme = darkTheme;
  let applyTheme = createTheme(indigoTheme);

  if (isDarkTheme) {
    document.body.classList.add('dark-theme');
    applyTheme = createTheme(darkTheme);
  } else {
    applyTheme = getColorTheme(themeColor, applyTheme);
  }

  if (location.pathname === '/') {
    if (authUser === null) {
      return <Redirect to={'/signin'} />;
    } else if (initURL === '' || initURL === '/' || initURL === '/signin') {
      return <Redirect to={'/app/dashboard'} />;
    } else {
      return <Redirect to={initURL} />;
    }
  }
  if (isDirectionRTL) {
    applyTheme.direction = 'rtl';
    document.body.classList.add('rtl');
  } else {
    document.body.classList.remove('rtl');
    applyTheme.direction = 'ltr';
  }

  return (
    <ThemeProvider theme={applyTheme}>
      <IntlProvider locale={currentAppLocale.locale} messages={currentAppLocale.messages}>
        <TimezoneProvider>
          {authLoading ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                fontSize: '24px',
                flexDirection: 'column',
                gap: '20px',
                backgroundColor: '#4b5258',
              }}
            >
              <IntlMessages id="appModule.loading" />
              <CircularProgress color="primary" size={60} />
            </div>
          ) : (
            <div className="app-main vpag-theme">
              <ToasterContainer />
              <Switch>
                <RestrictedRoute path={`${match.url}app`} authUser={authUser} component={AppLayout} />
                <Route exact path="/signin" component={SignIn} />
                <Route exact path="/signup">
                  <Redirect to="/signin" />
                </Route>
                <Route exact path="/forgot-password" component={ForgotPassword} />
                <Route exact path="/recover-password/:token" component={PasswordRecovery} />
              </Switch>
            </div>
          )}
        </TimezoneProvider>
      </IntlProvider>
    </ThemeProvider>
  );
};

export default App;
